<script setup lang="ts">
import {computed, onBeforeUnmount, ref} from "vue"
import {storeToRefs} from "pinia"
import {Mic, MicOff, Volume2, VolumeX} from "lucide-vue-next"
import {useGroupsStore} from "~/stores/groups"
import VoiceAvatar from "~/components/voice-ui/VoiceAvatar/VoiceAvatar.vue"
import {resolveMediaUrl} from "~/lib/mediaCache"

const props = defineProps<{
  channelName: string
  groupName?: string
  avatarUrl?: string | null
}>()

function getGroupInitials(name: string) {
  return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .substring(0, 2)
      .toUpperCase()
}

const emit = defineEmits<{
  (e: "expand"): void
}>()

const groupsStore = useGroupsStore()
const {isMicMuted, isDeafened} = storeToRefs(groupsStore)

type Corner = "top-left" | "top-right" | "bottom-left" | "bottom-right"

const Y_EDGE_MARGIN = 66
const X_EDGE_MARGIN = 16
const STORAGE_KEY = "iopta:call-bubble-corner"
// Dragging is a real pointer gesture, not just a tap — anything under this is treated as a click
// (re-expand the drawer) rather than a drag-to-reposition.
const DRAG_THRESHOLD = 6

function loadCorner(): Corner {
  if (typeof localStorage === "undefined") return "bottom-right"
  const saved = localStorage.getItem(STORAGE_KEY)
  return saved === "top-left" || saved === "top-right" || saved === "bottom-left" || saved === "bottom-right"
      ? saved
      : "bottom-right"
}

const corner = ref<Corner>(loadCorner())
const dragX = ref(0)
const dragY = ref(0)
const isDragging = ref(false)

let pointerId: number | null = null
let startClientX = 0
let startClientY = 0
let moved = false

const CORNER_STYLES: Record<Corner, Record<string, string>> = {
  "top-left": {
    top: `calc(env(safe-area-inset-top) + ${Y_EDGE_MARGIN}px)`,
    left: `calc(env(safe-area-inset-left) + ${X_EDGE_MARGIN}px)`
  },
  "top-right": {
    top: `calc(env(safe-area-inset-top) + ${Y_EDGE_MARGIN}px)`,
    right: `calc(env(safe-area-inset-right) + ${X_EDGE_MARGIN}px)`
  },
  "bottom-left": {
    bottom: `calc(env(safe-area-inset-bottom) + ${Y_EDGE_MARGIN}px)`,
    left: `calc(env(safe-area-inset-left) + ${X_EDGE_MARGIN}px)`
  },
  "bottom-right": {
    bottom: `calc(env(safe-area-inset-bottom) + ${Y_EDGE_MARGIN}px)`,
    right: `calc(env(safe-area-inset-right) + ${X_EDGE_MARGIN}px)`
  },
}

// `transform`/`transition` live in scoped CSS (below), not here — combining an inline `transform`
// with the `bubble-*` enter/leave classes' own `transform` wouldn't work, inline styles always
// win over classes regardless of specificity. Only the actual per-frame drag/corner *values* are
// bound here, as custom properties the scoped CSS's `transform` reads via `var()`.
const style = computed(() => ({
  ...CORNER_STYLES[corner.value],
  "--drag-x": `${dragX.value}px`,
  "--drag-y": `${dragY.value}px`,
}))

function handlePointerDown(event: PointerEvent) {
  pointerId = event.pointerId
  ;(event.currentTarget as HTMLElement).setPointerCapture(pointerId)
  startClientX = event.clientX
  startClientY = event.clientY
  moved = false
  isDragging.value = true
  window.addEventListener("pointermove", handlePointerMove)
  window.addEventListener("pointerup", handlePointerUp)
}

function handlePointerMove(event: PointerEvent) {
  if (pointerId === null) return
  const dx = event.clientX - startClientX
  const dy = event.clientY - startClientY
  if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) moved = true
  dragX.value = dx
  dragY.value = dy
}

function handlePointerUp(event: PointerEvent) {
  window.removeEventListener("pointermove", handlePointerMove)
  window.removeEventListener("pointerup", handlePointerUp)
  isDragging.value = false
  pointerId = null

  if (!moved) {
    dragX.value = 0
    dragY.value = 0
    emit("expand")
    return
  }

  // Snap to whichever of the 4 screen corners the pointer ended up closest to, based on which
  // half of the viewport (top/bottom, left/right) it's in — not the bubble's own rect, which can
  // lag a frame behind the last pointermove.
  const nextCorner: Corner = `${event.clientY < window.innerHeight / 2 ? "top" : "bottom"}-${
      event.clientX < window.innerWidth / 2 ? "left" : "right"
  }` as Corner

  corner.value = nextCorner
  dragX.value = 0
  dragY.value = 0
  localStorage.setItem(STORAGE_KEY, nextCorner)
}

onBeforeUnmount(() => {
  window.removeEventListener("pointermove", handlePointerMove)
  window.removeEventListener("pointerup", handlePointerUp)
})
</script>
<!--h-14 flex w-[calc(100%-2rem)] items-center gap-3 rounded-full border bg-card p-2 text-left transition-transform duration-150 active:scale-95-->
<template>
  <button
      type="button"
      class="floating-call-bubble fixed h-14 rounded-full z-40 flex touch-none flex-col items-start justify-center
      p-1
      gap-3 border bg-card text-card-foreground shadow-lg backdrop-blur"
      :class="{ 'is-dragging': isDragging }"
      :style="style"
      :aria-label="`Вернуться в звонок: ${channelName}`"
      @pointerdown="handlePointerDown"
  >
    <div class="flex gap-2 items-center pr-4">
      <VoiceAvatar
          class="size-12 bg-none"
          fallback-class="text-base"
          :src="resolveMediaUrl(avatarUrl, 'avatars')"
          :alt="groupName ?? channelName"
          :fallback="getGroupInitials(groupName ?? channelName)"
      />

      <div class="flex gap-3">
        <MicOff v-if="isMicMuted" class="min-w-5 text-destructive"/>
        <Mic v-else class="size-5 text-green-500"/>
        <VolumeX v-if="isDeafened" class="size-5 text-destructive"/>
        <Volume2 v-else class="size-5 text-muted-foreground"/>
      </div>
    </div>
  </button>
</template>

<style scoped>
/* `transform` lives here (not inline) specifically so the `bubble-*` mount/unmount transition
   classes below can also set it — an inline `transform` would always win over theirs regardless
   of class specificity. `--drag-x`/`--drag-y` are the only per-frame values actually bound via
   `:style`; `--bubble-scale` is owned by the transition classes. */
.floating-call-bubble {
  transform: translate(var(--drag-x, 0px), var(--drag-y, 0px)) scale(var(--bubble-scale, 1));
  transition: transform 0.2s ease, top 0.2s ease, bottom 0.2s ease, left 0.2s ease, right 0.2s ease;
}

.floating-call-bubble:active {
  --bubble-scale: 0.95;
}

.floating-call-bubble.is-dragging {
  transition: none;
}

.bubble-enter-active {
  transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.25s ease;
}

.bubble-leave-active {
  transition: transform 0.22s cubic-bezier(0.36, 0, 0.66, -0.56), opacity 0.15s ease;
}

.bubble-enter-from,
.bubble-leave-to {
  --bubble-scale: 0;
  opacity: 0;
}
</style>
